import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { PageHeader, Card, Button, Table, LoadingSpinner, Pagination } from '../components/UI';
import Select from '../components/Select';
import * as I from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { componentStyles } from '../theme';

const InvoiceExcelViewPage = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    /* ================= FINANCIAL YEAR ================= */

    const defaultFYStart = useMemo(() => {
        const d = new Date();
        return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
    }, []);

    const [selectedYear, setSelectedYear] = useState({
        value: defaultFYStart,
        label: `${defaultFYStart}-${defaultFYStart + 1}`
    });

    const fyOptions = useMemo(() =>
        Array.from({ length: 5 }, (_, i) => {
            const y = defaultFYStart - i;
            return { value: y, label: `${y}-${y + 1}` };
        }), [defaultFYStart]
    );

    /* ================= QUARTER ================= */

    const quarterOptions = [
        { value: 'all', label: 'All Quarters' },
        { value: 1, label: 'Q1 (Apr - Jun)' },
        { value: 2, label: 'Q2 (Jul - Sep)' },
        { value: 3, label: 'Q3 (Oct - Dec)' },
        { value: 4, label: 'Q4 (Jan - Mar)' }
    ];
    const [selectedQuarter, setSelectedQuarter] = useState(quarterOptions[0]);

    /* ================= VENDOR ================= */

    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState({
        value: 'all',
        label: 'All Vendors'
    });

    /* ================= SEARCH & FILTERS ================= */

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedTdsRule, setSelectedTdsRule] = useState({
        value: 'all',
        label: 'All TDS Rules'
    });

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    /* ================= PAGINATION ================= */

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalInvoices, setTotalInvoices] = useState(0);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (e) {
            return '-';
        }
    };
    const pageSize = 100;

    /* ================= FETCH VENDORS ================= */

    useEffect(() => {
        api.get('/vendors').then(res => {
            const list = res.data?.content || [];
            setVendors([
                { value: 'all', label: 'All Vendors' },
                ...list.map(v => ({ value: v.id, label: v.name }))
            ]);
        });
    }, []);

    /* ================= RESET PAGE ================= */

    useEffect(() => {
        setCurrentPage(0);
    }, [selectedYear, selectedQuarter, selectedVendor, selectedTdsRule, debouncedSearchTerm]);

    /* ================= FETCH INVOICES ================= */

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const params = {
                page: currentPage,
                size: pageSize,
                financialYear: selectedYear.value
            };

            if (selectedQuarter.value !== 'all') {
                params.quarter = Number(selectedQuarter.value);
            }

            if (selectedVendor.value !== 'all') {
                params.vendorIds = [selectedVendor.value];
            }

            if (selectedTdsRule.value !== 'all') {
                params.tdsRule = selectedTdsRule.value;
            }

            if (debouncedSearchTerm) {
                params.searchTerm = debouncedSearchTerm;
            }

            const res = await api.get('/invoices/excel-view', { params });

            setInvoices(res.data?.content || []);
            setTotalPages(res.data?.totalPages || 0);
            setTotalInvoices(res.data?.totalElements || 0);
        } catch (e) {
            console.error(e);
            setError('Failed to fetch invoices');
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedYear, selectedQuarter, selectedVendor, selectedTdsRule, debouncedSearchTerm]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    /* ================= TDS RULE OPTIONS ================= */

    const tdsRuleOptions = useMemo(() => {
        // Derive unique rules from current result + currently selected rule
        const rulesInResults = Array.from(new Set(invoices.map(i => i.tdsRule).filter(Boolean)));

        const options = [{ value: 'all', label: 'All TDS Rules' }];

        // Combine results
        const combinedRules = Array.from(new Set([...rulesInResults, ...(selectedTdsRule.value !== 'all' ? [selectedTdsRule.value] : [])]));

        combinedRules.sort().forEach(r => {
            options.push({ value: r, label: r });
        });

        return options;
    }, [invoices, selectedTdsRule]);

    /* ================= GROUP DATA ================= */

    const groupedData = useMemo(() => {
        const result = {};
        invoices.forEach(inv => {
            const section = inv.tdsRule || 'UNKNOWN TDS';
            const vendor = inv.vendorName || 'UNKNOWN VENDOR';

            if (!result[section]) result[section] = {};
            if (!result[section][vendor]) {
                result[section][vendor] = {
                    vendorName: inv.vendorName,
                    vendorPan: inv.vendorPan,
                    vendorGst: inv.vendorGst,
                    invoices: []
                };
            }

            result[section][vendor].invoices.push(inv);
        });
        return result;
    }, [invoices]);

    const headingText = `${selectedQuarter.label} – FY ${selectedYear.label}`;

    /* ================= EXPORT TO EXCEL ================= */

    const exportToExcel = () => {
        if (!invoices.length) {
            alert('No data to export');
            return;
        }

        const workbook = XLSX.utils.book_new();

        Object.entries(groupedData).forEach(([section, vendors]) => {
            const rows = [];

            rows.push([`TDS SECTION: ${section}`]);
            rows.push([]);

            Object.values(vendors).forEach(vendor => {
                rows.push([`Vendor: ${vendor.vendorName}`]);
                rows.push([`PAN: ${vendor.vendorPan} | GST: ${vendor.vendorGst}`]);
                rows.push([]);

                rows.push([
                    'Invoice #', 'Invoice Date', 'Base Amount', 'GST Amount', 'Total Amount',
                    'TDS %', 'TDS Amount', 'Invoice Payable', 'Invoice Paid Date',
                    'Default Months', 'TDS Interest', 'Final TDS Payable',
                    'TDS Paid Date', 'Challan #', 'Filing Qtr', 'Status', 'Remarks', 'TDS Challan Amt'
                ]);

                vendor.invoices.forEach(inv => {
                    rows.push([
                        inv.invoiceNumber ?? '',
                        inv.invoiceDate ?? '',
                        inv.subtotal ?? '',
                        inv.tax ?? '',
                        inv.totalAmount ?? '',
                        inv.tdsPercentage ?? '',
                        inv.tdsAmount ?? '',
                        inv.invoicePayable ?? '',
                        inv.invoicePaidDate ?? '',
                        inv.defaultMonths ?? '',
                        inv.tdsInterest ?? '',
                        inv.finalTdsPayable ?? '',
                        inv.challanPaidDate ?? '',
                        inv.tdsChallanNumber ?? '',
                        inv.filingQuarter ?? '',
                        inv.filingStatus ?? '',
                        inv.remarks ?? '',
                        inv.challanAmount ?? ''
                    ]);
                });

                rows.push([]);
                rows.push([]);
            });

            const sheet = XLSX.utils.aoa_to_sheet(rows);
            sheet['!cols'] = Array(17).fill({ wch: 18 });

            const safeName = section.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 31);
            XLSX.utils.book_append_sheet(workbook, sheet, safeName);
        });

        const fileName =
            `TDS_${selectedQuarter.label}_FY_${selectedYear.label}`
                .replace(/\s+/g, '_') + '.xlsx';

        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        saveAs(
            new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            fileName
        );
    };

    const resetFilters = () => {
        setSearchTerm('');
        setSelectedQuarter(quarterOptions[0]);
        setSelectedVendor({ value: 'all', label: 'All Vendors' });
        setSelectedTdsRule({ value: 'all', label: 'All TDS Rules' });
        setSelectedYear({ value: defaultFYStart, label: `${defaultFYStart}-${defaultFYStart + 1}` });
    };

    /* ================= UI ================= */

    return (
        <div className="min-h-screen bg-neutral-50 pb-12">
            <PageHeader title="Invoice Excel View" subtitle={headingText} />

            <div className="max-w-[1600px] mx-auto px-6 py-8">

                <Card className="mb-8 border-none shadow-xl bg-white/80 backdrop-blur-md">
                    <div className="p-2">
                        <div className="flex flex-wrap items-end gap-x-6 gap-y-6">

                            {/* Search Filter */}
                            <div className="w-full md:w-[220px]">
                                <label className={componentStyles.label}>Search</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                        <I.Search className="h-4 w-4 text-neutral-400" />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Invoice #"
                                        className={componentStyles.input.base + " !py-[11px] !pl-11"}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* FY Filter */}
                            <div className="w-full md:w-[150px]">
                                <Select
                                    label="Financial Year"
                                    options={fyOptions}
                                    value={selectedYear.value}
                                    onChange={(e) => {
                                        const opt = fyOptions.find(o => o.value === Number(e.target.value));
                                        if (opt) setSelectedYear(opt);
                                    }}
                                />
                            </div>

                            {/* Quarter Filter */}
                            <div className="w-full md:w-[180px]">
                                <Select
                                    label="Quarter"
                                    options={quarterOptions}
                                    value={selectedQuarter.value}
                                    onChange={(e) => {
                                        const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
                                        const opt = quarterOptions.find(o => o.value === val);
                                        if (opt) setSelectedQuarter(opt);
                                    }}
                                />
                            </div>

                            {/* Vendor Filter */}
                            <div className="w-full md:w-[250px]">
                                <Select
                                    label="Vendor"
                                    options={vendors}
                                    value={selectedVendor.value}
                                    onChange={(e) => {
                                        const opt = vendors.find(o => String(o.value) === e.target.value);
                                        if (opt) setSelectedVendor(opt);
                                    }}
                                />
                            </div>

                            {/* TDS Rule Filter */}
                            <div className="w-full md:w-[200px]">
                                <Select
                                    label="TDS Rule"
                                    options={tdsRuleOptions}
                                    value={selectedTdsRule.value}
                                    onChange={(e) => {
                                        const opt = tdsRuleOptions.find(o => o.value === e.target.value);
                                        if (opt) setSelectedTdsRule(opt);
                                    }}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-3 ml-auto pt-2">
                                <Button
                                    variant="outline"
                                    onClick={resetFilters}
                                    className="h-[46px] px-4"
                                    title="Reset all filters"
                                >
                                    <I.RotateCcw className="w-4 h-4 mr-2" />
                                    Reset
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={async () => {
                                        const uniqueChallanIds = [...new Set(invoices.flatMap(inv => inv.challanIds || []))];
                                        if (uniqueChallanIds.length === 0) {
                                            alert('No challan documents found for the current selection.');
                                            return;
                                        }

                                        try {
                                            const { documentApi } = require('../api');
                                            const response = await documentApi.downloadTdsChallanZip(uniqueChallanIds, false);
                                            const url = window.URL.createObjectURL(new Blob([response.data]));
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute('download', 'challans.zip');
                                            document.body.appendChild(link);
                                            link.click();
                                            link.remove();
                                        } catch (error) {
                                            console.error('Failed to download zip', error);
                                            alert('Failed to download challan ZIP.');
                                        }
                                    }}
                                    className="h-[46px] px-4"
                                >
                                    <I.Archive className="w-4 h-4 mr-2" />
                                    Download Challans
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={exportToExcel}
                                    className="h-[46px] px-6 shadow-lg shadow-primary-200 bg-primary-600 hover:bg-primary-700"
                                >
                                    <I.Download className="w-4 h-4 mr-2" />
                                    Export Excel
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>

                <div className="mb-6 flex items-center justify-between bg-white px-6 py-4 rounded-xl shadow-sm border border-neutral-100">
                    <div className="flex items-center gap-4">
                        <div className="p-2 bg-primary-100 rounded-lg">
                            <I.ListFilter className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                            <span className="text-sm text-neutral-500 font-medium">Record Summary</span>
                            <div className="text-xl font-bold text-neutral-900">
                                {totalInvoices} <span className="text-neutral-400 font-normal">Total Invoices Found</span>
                            </div>
                        </div>
                    </div>
                    {invoices.length > 0 && (
                        <div className="text-sm font-semibold text-primary-600 bg-primary-50 px-4 py-2 rounded-full border border-primary-100">
                            Showing page {currentPage + 1} of {totalPages}
                        </div>
                    )}
                </div>

                {/* DETAILS TABLE */}
                <Card className="overflow-hidden border-none shadow-xl bg-white">
                    {loading ? (
                        <div className="py-32 flex flex-col items-center justify-center">
                            <div className="relative">
                                <LoadingSpinner size="lg" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <I.Database className="w-6 h-6 text-primary-300 animate-pulse" />
                                </div>
                            </div>
                            <p className="mt-6 text-neutral-500 font-medium">Loading filtered records...</p>
                        </div>
                    ) : error ? (
                        <div className="py-32 text-center">
                            <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <I.AlertCircle className="w-10 h-10 text-red-500" />
                            </div>
                            <h3 className="text-lg font-bold text-neutral-900 mb-2">Oops! Something went wrong</h3>
                            <p className="text-neutral-500 mb-8 max-w-xs mx-auto">{error}</p>
                            <Button onClick={fetchInvoices} variant="primary" size="md">
                                <I.RefreshCw className="w-4 h-4 mr-2" />
                                Try Again
                            </Button>
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="py-32 text-center">
                            <div className="bg-neutral-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border border-neutral-100">
                                <I.SearchX className="w-10 h-10 text-neutral-300" />
                            </div>
                            <h3 className="text-lg font-bold text-neutral-900 mb-2">No results found</h3>
                            <p className="text-neutral-500 mb-8 max-w-sm mx-auto">
                                We couldn't find any invoices matching your current filter criteria. Try adjusting your search or filters.
                            </p>
                            <Button onClick={resetFilters} variant="outline" size="md">Clear All Filters</Button>
                        </div>
                    ) : (
                        <div className="p-8">
                            {Object.entries(groupedData).map(([section, vendors]) => (
                                <div key={section} className="mb-16 last:mb-0">
                                    <div className="flex items-center gap-4 mb-8">
                                        <div className="h-12 w-1.5 bg-primary-600 rounded-full"></div>
                                        <div>
                                            <h2 className="text-2xl font-extrabold text-neutral-900 tracking-tight">TDS Section: {section}</h2>
                                            <div className="flex items-center gap-2 text-neutral-500 text-sm mt-1">
                                                <I.Layers className="w-4 h-4" />
                                                <span>Grouped by Vendor compliance</span>
                                            </div>
                                        </div>
                                    </div>

                                    {Object.entries(vendors).map(([vendorName, vendor]) => (
                                        <div key={vendorName} className="mb-10 last:mb-0 ml-6 group">
                                            <div className="flex items-center justify-between mb-4 pr-4 transition-transform group-hover:translate-x-1 duration-300">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center border border-neutral-200 text-neutral-600 font-bold group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                                                        {vendor.vendorName.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-lg text-neutral-900 mb-0.5">{vendor.vendorName}</h3>
                                                        <div className="flex items-center gap-4 text-xs text-neutral-500 font-medium">
                                                            <span className="flex items-center gap-1.5 p-1 px-2 bg-neutral-100 rounded-md border border-neutral-200">
                                                                <I.Fingerprint className="w-3.5 h-3.5 text-neutral-400" />
                                                                PAN: <span className="text-neutral-800">{vendor.vendorPan || 'N/A'}</span>
                                                            </span>
                                                            <span className="flex items-center gap-1.5 p-1 px-2 bg-neutral-100 rounded-md border border-neutral-200">
                                                                <I.ShieldCheck className="w-3.5 h-3.5 text-neutral-400" />
                                                                GST: <span className="text-neutral-800">{vendor.vendorGst || 'N/A'}</span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end">
                                                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-1">Invoice Count</span>
                                                    <span className="text-sm font-bold text-neutral-800 bg-neutral-50 px-3 py-1 rounded-full border border-neutral-200">
                                                        {vendor.invoices.length} Records
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="overflow-x-auto rounded-2xl border border-neutral-200 shadow-sm bg-neutral-50/30">
                                                <Table
                                                    headers={[
                                                        'Invoice #', 'Date Recvd', 'Invoice Date', 'Base', 'GST', 'Total',
                                                        'TDS %', 'TDS Amt', 'Payable', 'Interest', 'Final TDS',
                                                        'Challan #', 'Paid Date', 'Filing Qtr', 'Status'
                                                    ]}
                                                    data={vendor.invoices.map(inv => [
                                                        <div className="flex flex-col">
                                                            <span className="font-mono font-bold text-neutral-900">{inv.invoiceNumber}</span>
                                                            {inv.remarks && <span className="text-[10px] text-neutral-400 truncate max-w-[120px]" title={inv.remarks}>{inv.remarks}</span>}
                                                        </div>,
                                                        <span className="text-neutral-600 text-sm whitespace-nowrap">{formatDate(inv.dateReceived)}</span>,
                                                        <span className="text-neutral-600 text-sm whitespace-nowrap">{formatDate(inv.invoiceDate)}</span>,
                                                        <span className="text-neutral-600 text-sm">₹{inv.subtotal?.toLocaleString()}</span>,
                                                        <span className="text-neutral-600 text-sm">₹{inv.tax?.toLocaleString()}</span>,
                                                        <span className="font-bold text-neutral-900">₹{inv.totalAmount?.toLocaleString()}</span>,
                                                        <span className="text-neutral-500 font-medium">{inv.tdsPercentage}%</span>,
                                                        <span className="text-neutral-900 font-medium">₹{inv.tdsAmount?.toLocaleString()}</span>,
                                                        <span className="text-primary-700 font-extrabold whitespace-nowrap">₹{inv.invoicePayable?.toLocaleString()}</span>,
                                                        <div className="flex flex-col items-center">
                                                            <span className="text-red-500 font-bold text-sm">₹{inv.tdsInterest || '0'}</span>
                                                            <span className="text-[9px] text-neutral-400 font-bold">{inv.defaultMonths || 0}m delay</span>
                                                        </div>,
                                                        <span className="text-red-600 font-extrabold whitespace-nowrap">₹{inv.finalTdsPayable?.toLocaleString()}</span>,
                                                        <span className="text-neutral-600 font-mono text-xs">{inv.tdsChallanNumber || '-'}</span>,
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-neutral-400 font-bold uppercase">Challan Date</span>
                                                            <span className="text-neutral-800 text-xs font-semibold">{inv.challanPaidDate ? formatDate(inv.challanPaidDate) : '-'}</span>
                                                        </div>,
                                                        <span className="text-neutral-800 text-xs font-semibold whitespace-nowrap">{inv.filingQuarter || '-'}</span>,
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black tracking-tighter ${inv.filingStatus === 'PAID'
                                                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                                            : 'bg-amber-100 text-amber-700 border border-amber-200'
                                                            }`}>
                                                            {inv.filingStatus || 'PENDING'}
                                                        </span>
                                                    ])}
                                                    emptyMessage="No invoices found for this vendor."
                                                    stickyFirstColumn={true}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                {!loading && totalPages > 1 && (
                    <div className="mt-12 flex justify-center">
                        <div className="bg-white p-2 rounded-2xl shadow-lg border border-neutral-100">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceExcelViewPage;
